import { groupBy } from 'lodash';
import { useEffect, useState } from 'react';
import { getCategories } from '../domain/geometric-operations';
import { convertToElementIdentifierDbidType } from '../utils/dbid-utils';
import { useViewer } from '../components/common/ForgeViewer';
import {
  CalculationTypeInput,
  ElementIdentifierDbidTypeInput,
  QuantityOperation,
  Unit,
  CurrencyEnum,
  QuantityEnum,
} from '../gql/graphql';
import { IfcClass } from '../domain/geometry/algorithm-map';
import {
  GeometricProperty,
  SPARKEL_GEOMETRIC_PROPERTY_SET,
} from '../domain/geometry/geometric-types';
import { useResolvedOrder } from './order';

const ELIGIBLE_IFC_CLASSES: Set<IfcClass> = new Set([
  IfcClass.Wall,
  IfcClass.Window,
  IfcClass.Slab,
]);

export type SuggestionInfo = {
  resourceName: string;
  suggestionLabel: string;
  dbIds: ElementIdentifierDbidTypeInput[];
  unit: Unit;
  currency: CurrencyEnum;
  quantity: {
    type: QuantityEnum;
    calculation: CalculationTypeInput;
  };
  customFields: {
    name: string;
    type: QuantityEnum;
    calculation?: CalculationTypeInput;
    staticValue?: string;
  }[];
};

type UseSuggestedResourcesReturn = {
  suggestedResources: SuggestionInfo[] | null;
};

export function useSuggestedResources(
  projectId: string,
  orderId: string
): UseSuggestedResourcesReturn {
  const [suggestedResources, setSuggestedResources] = useState<
    SuggestionInfo[] | null
  >(null);

  const order = useResolvedOrder(projectId, orderId);

  const { selectedDbIds, loadedModels, isAllModelsLoaded } = useViewer();

  useEffect(() => {
    async function loadSuggestedResourcesNoInsulation() {
      const dbIdsList = convertToElementIdentifierDbidType(selectedDbIds);

      if (dbIdsList.length === 0) {
        setSuggestedResources(null);
        return;
      }

      const modelDbids = dbIdsList.filter(
        (dbIds) => dbIds.modelUrn in loadedModels
      );
      const dbIdsWithIfcs = await getCategories(loadedModels, modelDbids);
      const dbIdsByIfcClass = groupBy(dbIdsWithIfcs, 'ifcClass');

      let suggestions: SuggestionInfo[] = [];

      for (const [ifcClass, elements] of Object.entries(dbIdsByIfcClass)) {
        if (!ELIGIBLE_IFC_CLASSES.has(ifcClass as IfcClass)) {
          continue;
        }

        const humanReadableIfcClass = ifcClass.replace(/^Ifc/, '');
        // NOTE: We uare harcoding the suggested columns for now
        // In the future we may consult some service to determine what resources to suggest, and how we name them.
        suggestions.push({
          // This or add the description to the name as well?
          resourceName: `${humanReadableIfcClass}s`,
          suggestionLabel: `(${elements.length}) ${humanReadableIfcClass}  - Net area, Gross area, Length`,
          dbIds: Object.entries(groupBy(elements, 'modelUrn')).map(
            ([modelUrn, elements]) => ({
              dbIds: elements.map((element) => element.dbId),
              modelUrn,
            })
          ),
          quantity: {
            type: QuantityEnum.Dynamic,
            calculation: {
              operation: QuantityOperation.Sum,
              attribute: {
                category: SPARKEL_GEOMETRIC_PROPERTY_SET,
                name: GeometricProperty.LARGEST_SIDE_AREA,
              },
            },
          },
          unit: Unit.M2,
          currency: CurrencyEnum.Nok,
          customFields: [
            {
              name: 'Gross area [m²]',
              type: QuantityEnum.Dynamic,
              calculation: {
                operation: QuantityOperation.Sum,
                attribute: {
                  category: SPARKEL_GEOMETRIC_PROPERTY_SET,
                  name: GeometricProperty.GROSS_SIDE_AREA,
                },
              },
            },
            {
              name: 'Length [m]',
              type: QuantityEnum.Dynamic,
              calculation: {
                operation: QuantityOperation.Sum,
                attribute: {
                  category: SPARKEL_GEOMETRIC_PROPERTY_SET,
                  name: GeometricProperty.LENGTH,
                },
              },
            },
          ],
        });
      }

      const totalNumberOfElements = dbIdsList.reduce(
        (acc, modelDbIds) => acc + modelDbIds.dbIds.length,
        0
      );

      suggestions.push({
        resourceName: 'Element count',
        suggestionLabel: `(${totalNumberOfElements}) - Count elements`,
        currency: CurrencyEnum.Nok,
        dbIds: dbIdsList,
        unit: Unit.Pcs,
        customFields: [],
        quantity: {
          type: QuantityEnum.Dynamic,
          calculation: {
            operation: QuantityOperation.Count,
          },
        },
      });

      setSuggestedResources(suggestions);
    }

    async function loadSuggestedResourcesInsulation() {
      let suggestions: SuggestionInfo[] = [];
      const dbIdsList = convertToElementIdentifierDbidType(selectedDbIds);

      const energyRequirement = order.resolvedOrder?.labels.find(
        (label) => label.key === 'Energy requirements'
      )?.value;
      if (energyRequirement && energyRequirement.includes('0.09')) {
        suggestions.push({
          // This or add the description to the name as well?
          resourceName: 'RW Toprock System 0,24m x 2,0m x 360mm - 15 st/pal',
          suggestionLabel: `RW Toprock System 0,24m x 2,0m x 360mm - 15 st/pal`,
          dbIds: dbIdsList,
          quantity: {
            type: QuantityEnum.Dynamic,
            calculation: {
              operation: QuantityOperation.Sum,
              attribute: {
                category: SPARKEL_GEOMETRIC_PROPERTY_SET,
                name: GeometricProperty.LARGEST_SIDE_AREA,
              },
            },
          },
          unit: Unit.M2,
          currency: CurrencyEnum.Nok,
          customFields: [
            {
              name: 'Artikelnummer',
              type: QuantityEnum.Static,
              staticValue: 'RW276284',
            },
          ],
        });

        suggestions.push({
          // This or add the description to the name as well?
          resourceName: 'RW Underlag Energy 0,6m x 2,0m x 50mm - 48 St/pal',
          suggestionLabel: `RW Underlag Energy 0,6m x 2,0m x 50mm - 48 St/pal`,
          dbIds: dbIdsList,
          quantity: {
            type: QuantityEnum.Dynamic,
            calculation: {
              operation: QuantityOperation.Sum,
              attribute: {
                category: SPARKEL_GEOMETRIC_PROPERTY_SET,
                name: GeometricProperty.LARGEST_SIDE_AREA,
              },
            },
          },
          unit: Unit.M2,
          currency: CurrencyEnum.Nok,
          customFields: [
            {
              name: 'Artikelnummer',
              type: QuantityEnum.Static,
              staticValue: 'RW274399',
            },
          ],
        });
      } else if (energyRequirement && energyRequirement.includes('0.12')) {
        suggestions.push({
          // This or add the description to the name as well?
          resourceName: 'Underlag 70mm',
          suggestionLabel: `Underlag 70mm`,
          dbIds: dbIdsList,
          quantity: {
            type: QuantityEnum.Dynamic,
            calculation: {
              operation: QuantityOperation.Sum,
              attribute: {
                category: SPARKEL_GEOMETRIC_PROPERTY_SET,
                name: GeometricProperty.LARGEST_SIDE_AREA,
              },
            },
          },
          unit: Unit.M2,
          currency: CurrencyEnum.Nok,
          customFields: [
            {
              name: 'Artikelnummer',
              type: QuantityEnum.Static,
              staticValue: 'RW274492',
            },
          ],
        });

        suggestions.push({
          // This or add the description to the name as well?
          resourceName: 'RW Toprock System 0,24m x 2,0m x 230mm - 25 st/pal',
          suggestionLabel: `RW Toprock System 0,24m x 2,0m x 230mm - 25 st/pal`,
          dbIds: dbIdsList,
          quantity: {
            type: QuantityEnum.Dynamic,
            calculation: {
              operation: QuantityOperation.Sum,
              attribute: {
                category: SPARKEL_GEOMETRIC_PROPERTY_SET,
                name: GeometricProperty.LARGEST_SIDE_AREA,
              },
            },
          },
          unit: Unit.M2,
          currency: CurrencyEnum.Nok,
          customFields: [
            {
              name: 'Artikelnummer',
              type: QuantityEnum.Static,
              staticValue: 'RW276280',
            },
          ],
        });
      }

      setSuggestedResources(suggestions);
    }
    if (isAllModelsLoaded) {
      loadSuggestedResourcesNoInsulation();
    }
  }, [
    selectedDbIds,
    isAllModelsLoaded,
    loadedModels,
    order.resolvedOrder?.labels,
  ]);

  return {
    suggestedResources,
  };
}
